/* File: src/contexts/game/gameSetup.js */
import { useCallback } from 'react';
import { auth } from '../../firebaseConfig'; // Import Firebase auth instance

// Import the frontend wrapper function for the backend call
import { generateSequenceHttp } from './gameCloudFunctions';

// Import helpers (assuming they are correctly defined and exported in the frontend version)
// Frontend version might only have formatters etc. - calculateSessionParameters typically runs backend-side.
// import { calculateSessionParameters } from '../gameHelpers';

/**
 * Factory function returning an object containing the *logic* for setting up a game.
 * These are plain functions, NOT wrapped in useCallback here; the wrapping happens in GameContext.
 * Handles starting standard games, playground games, challenge attempts, and retries.
 *
 * @param {object} params - Dependencies required for setup actions.
 * @param {object|null} params.activeProfile - The currently active user profile object.
 * @param {boolean} params.saveToProfileState - Current value of the saveToProfile state.
 * @param {object} params.challengeState - Current challenge state { isActive, targetNBack, wins }.
 * @param {boolean} params.isPlaygroundSessionState - Current value of isPlaygroundSession state.
 * @param {Function} params.setGameState - Setter for game state.
 * @param {Function} params.setNBack - Setter for standard n-back state.
 * @param {Function} params.setDifficulty - Setter for standard difficulty state.
 * @param {Function} params.setSequence - Setter for game sequence state.
 * @param {Function} params.setCurrentTrial - Setter for current trial index state.
 * @param {Function} params.setTotalTrials - Setter for total trials state.
 * @param {Function} params.setGameResults - Setter for game results state.
 * @param {Function} params.setFeedbackMessage - Setter for feedback message state.
 * @param {Function} params.setIsPlaygroundSession - Setter for playground flag.
 * @param {Function} params.setSaveToProfile - Setter for the save-to-profile flag state.
 * @param {Function} params.setPlaygroundSettings - Setter for playground settings.
 * @param {Function} params.startCountdown - Action to initiate the countdown.
 * @param {Function} params.setChallengeState - Setter for challenge state.
 * @param {Function} params.setConsecutiveSuccesses - Setter for standard consecutive successes state.
 * @param {Function} params.setShowCountdown - Setter for showing/hiding countdown UI.
 * @param {Function} params.setIsCountdownVisuallyComplete - Setter for countdown animation flag.
 * @param {React.MutableRefObject<NodeJS.Timeout|null>} params.timerRef - Ref for the game timer.
 * @param {React.MutableRefObject<boolean>} params.isEndingRef - Ref flag to prevent concurrent endGame executions.
 * @param {React.MutableRefObject<Array<number>>} params.visualResponsesRef - Ref for visual responses.
 * @param {React.MutableRefObject<Array<number>>} params.audioResponsesRef - Ref for audio responses.
 * @param {React.MutableRefObject<Array<object>>} params.visualMatchesRef - Ref for storing calculated visual matches.
 * @param {React.MutableRefObject<Array<object>>} params.audioMatchesRef - Ref for storing calculated audio matches.
 * @param {React.MutableRefObject<object|null>} params.sessionSettingsRef - Ref for storing generated session parameters.
 * @param {React.MutableRefObject<Array<object>>} params.replayEventsRef - Ref for replay event log.
 * @param {React.MutableRefObject<number|null>} params.sessionStartTimeRef - Ref for session start timestamp.
 * @param {React.MutableRefObject<Function|null>} params.onNavigateRef - Ref holding the navigation function.
 * @param {React.MutableRefObject<string>} params.latestGameStateRef - Ref holding the latest gameState.
 * @param {object} params.audioManager - Instance of the audio manager.
 * @returns {object} Object containing setup action logic functions: { startGameLogic, startPlaygroundGameLogic, cancelPreparationLogic }.
 */
export function createGameSetupActions({
    activeProfile, saveToProfileState, challengeState, isPlaygroundSessionState,
    setGameState, setNBack, setDifficulty, setSequence, setCurrentTrial,
    setTotalTrials, setGameResults, setFeedbackMessage, setIsPlaygroundSession,
    setSaveToProfile, setPlaygroundSettings, startCountdown, setChallengeState,
    setConsecutiveSuccesses, setShowCountdown, setIsCountdownVisuallyComplete,
    timerRef, isEndingRef, visualResponsesRef, audioResponsesRef, visualMatchesRef,
    audioMatchesRef, sessionSettingsRef, replayEventsRef, sessionStartTimeRef,
    onNavigateRef, latestGameStateRef, audioManager
}) {

    /**
     * Core logic for starting a game session (standard, challenge, override).
     * Fetches sequence from backend, resets state, and triggers countdown.
     * @param {object} [options={}] - Optional settings overrides.
     * @param {boolean} [options.isChallengeAttempt] - Flag indicating a challenge attempt.
     * @param {number} [options.nBack] - N-Back level for challenge attempt.
     * @param {number} [options.difficulty] - Difficulty for challenge attempt (usually ignored/set to max).
     * @param {number} [options.overrideNBack] - Specific N-Back level for retry/override.
     * @param {number} [options.overrideDifficulty] - Specific difficulty level for retry/override.
     */
    const startGameLogic = async (options = {}) => {
        const functionStartTime = Date.now(); // For timing debug
        console.log(`[startGameLogic ${functionStartTime}] Initiated with options:`, JSON.stringify(options));

        // --- Prerequisite Checks ---
        if (!auth.currentUser) {
            console.error(`[startGameLogic ${functionStartTime}] Aborted: User is not authenticated.`);
            setFeedbackMessage("Authentication error. Please log in again.");
            setGameState('idle'); setShowCountdown(false); return;
        }
        const currentState = latestGameStateRef.current;
        if (['preparing', 'playing', 'paused'].includes(currentState)) {
             console.warn(`[startGameLogic ${functionStartTime}] Aborted: Game already in progress or preparing (state: ${currentState}).`);
             // Avoid resetting state if already preparing to prevent disrupting an ongoing valid start.
             return;
        }
        // --- End Prerequisite Checks ---

        // --- Reset State (Critical to run *before* parameter determination if relying on profile state) ---
        console.log(`[startGameLogic ${functionStartTime}] Current state is ${currentState}. Resetting state for new game...`);
        setGameState('preparing'); // Set preparing FIRST
        setGameResults(null); setFeedbackMessage(''); setCurrentTrial(-1);
        setSequence([]); setTotalTrials(0); setIsPlaygroundSession(false);
        setPlaygroundSettings(null); // Ensure playground is off
        setShowCountdown(false); setIsCountdownVisuallyComplete(false);
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        isEndingRef.current = false; visualResponsesRef.current = []; audioResponsesRef.current = [];
        visualMatchesRef.current = []; audioMatchesRef.current = []; sessionSettingsRef.current = null;
        replayEventsRef.current = []; sessionStartTimeRef.current = null;
        // --- End Reset State ---

        // --- Determine Game Parameters ---
        let nBackToUse = null; // Initialize to null for clearer detection of failure
        let difficultyToUse = null;
        let isChallengeAttemptFlag = false;
        let sessionType = 'UNKNOWN'; // For logging

        // --- VERY IMPORTANT: Check Challenge FIRST ---
        if (options.isChallengeAttempt === true && typeof options.nBack === 'number' && options.nBack >= 1) {
            sessionType = 'CHALLENGE (Explicit Options)';
            console.log(`[startGameLogic ${functionStartTime} - Param Det] Path taken: ${sessionType}`);
            nBackToUse = options.nBack;
            difficultyToUse = 0.99; // Challenge is always max difficulty for the N level
            isChallengeAttemptFlag = true;
            // Explicitly set the challenge state in context *immediately*
            setChallengeState({ isActive: true, targetNBack: nBackToUse, wins: 0 });
            // Save flag is handled by endGame logic based on isChallengeAttemptFlag
            // Set context flag - backend/endGame will interpret correctly
            // Note: setSaveToProfile(true) is needed so the backend processes it,
            // but endGame logic won't update profile progress for challenges.
            setSaveToProfile(true);

        // --- Check Override SECOND ---
        } else if (options.overrideNBack !== undefined && options.overrideDifficulty !== undefined) {
            sessionType = 'OVERRIDE';
            console.log(`[startGameLogic ${functionStartTime} - Param Det] Path taken: ${sessionType}`);
            nBackToUse = options.overrideNBack;
            difficultyToUse = Math.max(0, Math.min(0.99, options.overrideDifficulty)); // Clamp override (0.00-0.99)
            isChallengeAttemptFlag = false;
            // Ensure challenge state is inactive for overrides
            setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
            setSaveToProfile(true); // Save results for overrides/retries

        // --- Check Standard Profile THIRD ---
        } else if (activeProfile) {
            sessionType = 'STANDARD (Profile)';
            console.log(`[startGameLogic ${functionStartTime} - Param Det] Path taken: ${sessionType}`);
            nBackToUse = activeProfile.currentNBack ?? 2;
            // Ensure difficulty from profile is clamped correctly (0.00-0.99 range allowed for standard start)
            difficultyToUse = Math.max(0, Math.min(0.99, activeProfile.currentDifficulty ?? 0.0));
            isChallengeAttemptFlag = false; // Standard game is not a challenge attempt
            // Ensure challenge state is inactive for standard games, reset if needed
             if(challengeState.isActive) {
                  console.warn(`[startGameLogic ${functionStartTime}] Resetting active challenge state for standard game start.`);
                  setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
             }
            setSaveToProfile(saveToProfileState); // Use existing save preference from context state for standard

        // --- Fallback Error FOURTH ---
        } else {
            sessionType = 'ERROR (No Profile/Challenge/Override)';
            console.error(`[startGameLogic ${functionStartTime} - Param Det] Critical error: ${sessionType}. Cannot determine parameters.`);
            setFeedbackMessage("Internal error: Could not determine game level. Profile missing?");
            setGameState('idle'); // Revert state
             // If challenge flag was somehow true in options but nBack was invalid, reset it
             if (options.isChallengeAttempt === true) {
                  setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
             }
            return;
        }
        // --- End Determine Game Parameters ---


        // --- Log Determined Parameters ---
         console.log(`[startGameLogic ${functionStartTime} - Param Det] FINAL PARAMS: Type=${sessionType}, N=${nBackToUse}, Diff=${difficultyToUse}, isChallenge=${isChallengeAttemptFlag}`);
         // Sanity check - if any are null/undefined here, something is wrong
         if (nBackToUse === null || difficultyToUse === null) {
              console.error(`[startGameLogic ${functionStartTime}] CRITICAL INTERNAL ERROR: Parameters nBackToUse or difficultyToUse are null after determination block! Aborting.`);
              setFeedbackMessage("Internal error: Failed to set game parameters correctly.");
              setGameState('idle');
              // Reset challenge state if it was potentially set incorrectly
               if (isChallengeAttemptFlag) {
                    setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
               }
              return;
         }
        // --- End Log ---


        // --- Update Context State (with precisely determined values) ---
        const saveFlagToLog = saveToProfileState; // Capture for log before potential change below
        console.log(`[startGameLogic ${functionStartTime}] Setting context state: N=${nBackToUse}, Difficulty=${difficultyToUse}, Save=${saveFlagToLog} (Note: 'saveToProfile' state might be true even for challenges, handled later)`);
        setNBack(nBackToUse);
        setDifficulty(difficultyToUse); // Store the precise 0.00-0.99 difficulty
        // Reset consecutive successes ONLY if it's NOT a standard game continuing the streak
        if (sessionType !== 'STANDARD (Profile)') {
             console.log(`[startGameLogic ${functionStartTime}] Resetting consecutive successes because session type is ${sessionType}.`);
             setConsecutiveSuccesses(0);
        } else if (activeProfile) {
             // For standard games, ensure streak is synced from profile BEFORE starting
             const profileStreak = activeProfile.consecutiveSuccesses || 0;
             console.log(`[startGameLogic ${functionStartTime}] Syncing consecutive successes from profile for STANDARD game: ${profileStreak}.`);
             setConsecutiveSuccesses(profileStreak);
        }
        // --- End Update Context State ---


        // --- Log Setup Event for Replay ---
        const setupTimestamp = Date.now();
        replayEventsRef.current.push({
            type: 'gameSetup',
            timestamp: setupTimestamp,
            nBack: nBackToUse,
            difficulty: difficultyToUse, // Log precise difficulty used
            isChallenge: isChallengeAttemptFlag,
            isPlayground: false, // This is startGameLogic, not playground
            isOverride: sessionType === 'OVERRIDE',
            profileId: activeProfile?.id || null
        });
        // --- End Log Setup Event ---


        // --- Fetch Sequence from Backend ---
        console.log(`[startGameLogic ${functionStartTime}] Preparing to call generateSequenceHttp. N=${nBackToUse}, D(Precise)=${difficultyToUse}, isChallenge=${isChallengeAttemptFlag}`);
        try {
            // Convert precise difficulty (0.00-0.99) to conceptual seed (0.0-1.0) for backend generator/helpers
            // Handle the edge case of 0.99 difficulty mapping to 1.0 seed
            const difficultySeedForBackend = difficultyToUse >= 0.99 ? 1.0 : (difficultyToUse / 0.99);
            console.log(`[startGameLogic ${functionStartTime}] Calculated difficultySeedForBackend: ${difficultySeedForBackend} (from difficultyToUse: ${difficultyToUse})`);

            const backendCallStartTime = Date.now();
            // Call the cloud function wrapper
            const response = await generateSequenceHttp({
                nBack: nBackToUse,
                difficulty: difficultySeedForBackend, // Pass 0-1 seed
                isPlayground: false, // Explicitly false for this function
                isChallengeAttempt: isChallengeAttemptFlag, // Pass the determined flag
                profileId: activeProfile?.id || null,
            });
            const backendCallEndTime = Date.now();
            console.log(`[startGameLogic ${functionStartTime}] Backend call generateSequenceHttp took ${backendCallEndTime - backendCallStartTime} ms.`);


            // Validate backend response
            if (!response || !response.sequence || !response.parameters) {
                throw new Error('Invalid sequence response structure from backend.');
            }
             if (!Array.isArray(response.sequence)) {
                  throw new Error('Backend returned non-array sequence.');
             }
             if (typeof response.parameters?.totalTrials !== 'number' || response.parameters.totalTrials <= 0) {
                  throw new Error(`Backend returned invalid totalTrials: ${response.parameters?.totalTrials}`);
             }
            console.log(`[startGameLogic ${functionStartTime}] Sequence received from backend. Trials: ${response.parameters.totalTrials}, Seq Length: ${response.sequence.length}`);


            // Store session settings in ref (use precise difficulty for consistency in frontend checks)
            sessionSettingsRef.current = {
                ...response.parameters, // Includes calculatedISI, stimulusDuration, totalTrials etc.
                nBack: nBackToUse, // Store the N-Back used for this session
                difficulty: difficultyToUse, // Store precise 0.00-0.99 difficulty used
                isPlayground: false,
                isChallengeAttempt: isChallengeAttemptFlag, // Store challenge status
                showReport: true, // Default for non-playground
                generatedSequence: response.sequence // Store raw sequence if needed later
            };
            console.log(`[startGameLogic ${functionStartTime}] sessionSettingsRef updated:`, JSON.stringify(sessionSettingsRef.current));


            // Update context state with sequence data
            setSequence(response.sequence);
            setTotalTrials(response.parameters.totalTrials);
            visualMatchesRef.current = response.visualMatches || []; // Store match indices from backend
            audioMatchesRef.current = response.audioMatches || [];

            // Log sequence generation success event
            replayEventsRef.current.push({
                type: 'sequenceGenerated',
                timestamp: Date.now(),
                sequenceLength: response.sequence.length,
                totalTrials: response.parameters.totalTrials,
            });

            // Setup complete, trigger the countdown UI
            console.log(`[startGameLogic ${functionStartTime}] Setup complete. Calling startCountdown...`);
            startCountdown(); // This handles setting gameState to 'preparing', showing UI, preload etc.

        } catch (error) {
             const errorTime = Date.now();
             const setupDurationOnError = errorTime - setupTimestamp; // Time from setup log to error catch
             console.error(`[startGameLogic ${functionStartTime}] Error during backend call or processing (occurred after ${setupDurationOnError} ms from setup log):`, error);
             setFeedbackMessage(`Error starting game: ${error.message}. Please try again.`);
             setGameState('idle'); // Revert state to idle on failure
             setShowCountdown(false);
             // Clear potentially partial state to ensure clean start next time
             setSequence([]); setTotalTrials(0); sessionSettingsRef.current = null;
             visualMatchesRef.current = []; audioMatchesRef.current = []; replayEventsRef.current = [];
             // Also reset challenge state if it was set during a failed attempt
             if(isChallengeAttemptFlag) {
                 console.log(`[startGameLogic ${functionStartTime} - Error Handling] Resetting challenge state due to error.`);
                 setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
             }
        } finally {
             const functionEndTime = Date.now();
             console.log(`[startGameLogic ${functionStartTime}] Execution finished. Total time: ${functionEndTime - functionStartTime} ms.`);
        }
        // --- End Fetch Sequence ---
    };


    /**
     * Logic for starting a playground game session.
     * @param {object} settings - Playground settings { nBack, difficulty, trials, isi, stimDuration, showReport }.
     */
    const startPlaygroundGameLogic = async (settings) => {
        const functionStartTime = Date.now();
        console.debug(`[startPlaygroundGameLogic ${functionStartTime}] Initiated with settings:`, settings);

        // --- Prerequisite & Validation Checks ---
        if (!settings || typeof settings.nBack !== 'number' || typeof settings.difficulty !== 'number') {
            console.error(`[startPlaygroundGameLogic ${functionStartTime}] Aborted: Invalid playground settings provided.`);
            setFeedbackMessage("Invalid playground settings."); setGameState('idle'); return;
        }
         const currentState = latestGameStateRef.current;
         if (['preparing', 'playing', 'paused'].includes(currentState)) {
             console.warn(`[startPlaygroundGameLogic ${functionStartTime}] Aborted: Game already in progress or preparing (state: ${currentState}).`);
             return;
         }
        // --- End Checks ---

        // --- Reset State ---
        console.log(`[startPlaygroundGameLogic ${functionStartTime}] Current state is ${currentState}. Resetting state for playground...`);
        setGameState('preparing'); // Set preparing FIRST
        setGameResults(null); setFeedbackMessage(''); setCurrentTrial(-1);
        setSequence([]); setTotalTrials(0); setIsPlaygroundSession(true); // Set playground flag
        setPlaygroundSettings(settings); setSaveToProfile(false); // Never save playground progress
        setShowCountdown(false); setIsCountdownVisuallyComplete(false);
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        isEndingRef.current = false; visualResponsesRef.current = []; audioResponsesRef.current = [];
        visualMatchesRef.current = []; audioMatchesRef.current = []; sessionSettingsRef.current = null;
        replayEventsRef.current = []; sessionStartTimeRef.current = null;
        setChallengeState({ isActive: false, targetNBack: null, wins: 0 }); // Ensure not in challenge mode
        // --- End Reset State ---


        // --- Determine Parameters ---
        const nBackToUse = settings.nBack;
        const difficultyToUse = Math.max(0, Math.min(0.99, settings.difficulty)); // Clamp playground difficulty (0.00-0.99)
        const totalTrialsToUse = settings.trials || 20; // Default trials if not provided
        const showReport = settings.showReport !== false; // Default to show report
        const customIsi = settings.isi; // Can be undefined
        const customStimDuration = settings.stimDuration; // Can be undefined
        // --- End Determine Parameters ---


        // --- Update Context State ---
        console.log(`[startPlaygroundGameLogic ${functionStartTime}] Setting context state: N=${nBackToUse}, Difficulty=${difficultyToUse}`);
        setNBack(nBackToUse);
        setDifficulty(difficultyToUse); // Set precise difficulty
        setConsecutiveSuccesses(0); // Reset streak for playground
        // --- End Update Context State ---


        // --- Log Setup Event ---
        const setupTimestamp = Date.now();
        replayEventsRef.current.push({
            type: 'gameSetup', timestamp: setupTimestamp, nBack: nBackToUse, difficulty: difficultyToUse,
            isChallenge: false, isPlayground: true, settings: settings, profileId: activeProfile?.id || null
        });
        // --- End Log Setup Event ---


        // --- Fetch Sequence from Backend ---
        console.log(`[startPlaygroundGameLogic ${functionStartTime}] Preparing to call generateSequenceHttp for playground...`);
        try {
             const difficultySeedForBackend = difficultyToUse >= 0.99 ? 1.0 : (difficultyToUse / 0.99); // Normalize 0.00-0.99 -> 0.0-1.0
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Calculated difficultySeedForBackend: ${difficultySeedForBackend}`);

             const playgroundParams = {
                trials: totalTrialsToUse,
                ...(typeof customIsi === 'number' && { isi: customIsi }), // Only include if number
                ...(typeof customStimDuration === 'number' && { stimDuration: customStimDuration }) // Only include if number
            };
            console.log(`[startPlaygroundGameLogic ${functionStartTime}] Playground params for backend:`, playgroundParams);

             const backendCallStartTime = Date.now();
             const response = await generateSequenceHttp({
                 nBack: nBackToUse,
                 difficulty: difficultySeedForBackend, // Pass 0-1 seed
                 isPlayground: true,
                 isChallengeAttempt: false, // Never a challenge in playground
                 profileId: activeProfile?.id || null, // Pass profile ID (can be null)
                 playgroundParams: playgroundParams
             });
            const backendCallEndTime = Date.now();
            console.log(`[startPlaygroundGameLogic ${functionStartTime}] Backend call generateSequenceHttp took ${backendCallEndTime - backendCallStartTime} ms.`);


             if (!response || !response.sequence || !response.parameters) { throw new Error('Invalid sequence response from backend for playground.'); }
             if (!Array.isArray(response.sequence)) { throw new Error('Backend returned non-array sequence for playground.'); }
             if (typeof response.parameters?.totalTrials !== 'number' || response.parameters.totalTrials <= 0) { throw new Error(`Backend returned invalid totalTrials for playground: ${response.parameters?.totalTrials}`); }
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Sequence received. Trials: ${response.parameters.totalTrials}, Seq Length: ${response.sequence.length}`);


             // Store session settings
             sessionSettingsRef.current = {
                  ...response.parameters, // Includes calculated ISI, stim duration, totalTrials etc. from backend
                  nBack: nBackToUse,
                  difficulty: difficultyToUse, // Store precise difficulty
                  isPlayground: true,
                  isChallengeAttempt: false,
                  showReport: showReport, // Store user preference
                  generatedSequence: response.sequence
             };
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] sessionSettingsRef updated:`, JSON.stringify(sessionSettingsRef.current));

             // Update state with received data
             setSequence(response.sequence);
             setTotalTrials(response.parameters.totalTrials);
             visualMatchesRef.current = response.visualMatches || [];
             audioMatchesRef.current = response.audioMatches || [];

             // Log generation success
             replayEventsRef.current.push({ type: 'sequenceGenerated', timestamp: Date.now(), sequenceLength: response.sequence.length, totalTrials: response.parameters.totalTrials });

             // Start countdown
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Setup complete. Calling startCountdown...`);
             startCountdown();

        } catch (error) {
             const errorTime = Date.now();
             const setupDurationOnError = errorTime - setupTimestamp;
             console.error(`[startPlaygroundGameLogic ${functionStartTime}] Error during backend call or processing (occurred after ${setupDurationOnError} ms from setup log):`, error);
             setFeedbackMessage(`Error starting playground: ${error.message}`);
             setGameState('idle'); setShowCountdown(false);
             // Clear state
             setSequence([]); setTotalTrials(0); sessionSettingsRef.current = null;
             visualMatchesRef.current = []; audioMatchesRef.current = []; replayEventsRef.current = [];
             setIsPlaygroundSession(false); setPlaygroundSettings(null); // Reset playground flags on error
        } finally {
             const functionEndTime = Date.now();
             console.log(`[startPlaygroundGameLogic ${functionStartTime}] Execution finished. Total time: ${functionEndTime - functionStartTime} ms.`);
        }
        // --- End Fetch Sequence ---
    };


     /**
     * Logic to cancel the game preparation phase (e.g., user closes countdown).
     * Resets relevant state back to idle.
     */
    const cancelPreparationLogic = () => {
        const currentState = latestGameStateRef.current;
        console.debug(`[cancelPreparationLogic] Called with state: ${currentState}`);
        if (currentState === 'preparing') {
            console.log('[cancelPreparationLogic] Cancelling preparation...');
            setGameState('idle');
            setShowCountdown(false); setIsCountdownVisuallyComplete(false); setFeedbackMessage('');
            setSequence([]); setTotalTrials(0); sessionSettingsRef.current = null;
            visualMatchesRef.current = []; audioMatchesRef.current = []; replayEventsRef.current = [];
            if (isPlaygroundSessionState) {
                console.log('[cancelPreparationLogic] Resetting playground state.');
                setIsPlaygroundSession(false); setPlaygroundSettings(null);
            }
            // Reset challenge state if prep was cancelled during challenge setup
            // Check the sessionSettingsRef as well, as challengeState might lag
            const wasChallengeSetup = sessionSettingsRef.current?.isChallengeAttempt || challengeState.isActive;
            if (wasChallengeSetup) {
                 console.log("[cancelPreparationLogic] Resetting active challenge state due to preparation cancellation.");
                 setChallengeState({ isActive: false, targetNBack: null, wins: 0 });
            }
            console.log('[cancelPreparationLogic] State reset to idle.');
        } else {
             console.debug('[cancelPreparationLogic] No action taken, state is not "preparing".');
        }
    };


    // Return the logic functions
    return {
        startGameLogic,
        startPlaygroundGameLogic,
        cancelPreparationLogic,
    };
}